import {
  useBuildingPackageId,
  useGetBuildingPackageQuery,
  useProjectId,
  useServiceName,
  useUpdateBuildingPackageMutation,
} from '@consigli/hooks';
import { BuildingPackageWorkflowStep, PipelineName } from '@consigli/types';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useCreatePackageUtil } from '@/features/package-creation/hooks/use-create-package';
import { Route } from '@/routes';

import { BuildingLevelOverview } from './building-level-overview';
import { RoomLevelDimensioningCriteria } from './room-level-dimensioning-criteria';
import { SpecificationComplete } from './specification-complete';

enum ReviewStep {
  SPECIFICATION = 'SPECIFICATION',
  BUILDING_OVERVIEW = 'BUILDING_OVERVIEW',
  ROOM_CRITERIA = 'ROOM_CRITERIA',
}

export const UserReviewWrapper: React.FC = () => {
  const navigate = useNavigate();
  const projectId = useProjectId();
  const buildingPackageId = useBuildingPackageId();
  const { data: buildingPackage, isLoading } = useGetBuildingPackageQuery({
    projectId,
    buildingPackageId,
  });
  const serviceName = useServiceName();
  const [currentStep, setCurrentStep] = useState<ReviewStep>(ReviewStep.SPECIFICATION);
  const { startAnalysis } = useCreatePackageUtil({
    setCurrentCounter: () => {},
    setTargetCounter: () => {},
  });
  const [updateBuildingPackage] = useUpdateBuildingPackageMutation();

  const handleReviewValues = useCallback(() => {
    setCurrentStep(ReviewStep.BUILDING_OVERVIEW);
  }, []);

  const handleBuildingOverviewContinue = useCallback(() => {
    setCurrentStep(ReviewStep.ROOM_CRITERIA);
  }, []);

  const handleRoomCriteriaApprove = useCallback(() => {
    setCurrentStep(ReviewStep.SPECIFICATION);
  }, []);

  const handleLoadCalculation = useCallback(async () => {
    try {
      await updateBuildingPackage({
        projectId,
        buildingPackageId,
        data: { workflow_step: BuildingPackageWorkflowStep.PROCESSING_LOAD_CALCULATION_STEP },
      }).unwrap();

      await startAnalysis(buildingPackageId, PipelineName.LOAD_CALCULATION);

      navigate(
        `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.PROCESSING_LOAD_CALCULATION}`,
      );
    } catch (error) {
      console.error(error);
    }
  }, [buildingPackageId, startAnalysis, updateBuildingPackage, projectId, navigate, serviceName]);

  if (
    buildingPackage?.workflowStep ===
      BuildingPackageWorkflowStep.PROCESSING_SPEC_REQUIREMENTS_STEP ||
    buildingPackage?.workflowStep === BuildingPackageWorkflowStep.USER_REVIEW_SPEC_REQUIREMENTS_STEP
  ) {
    switch (currentStep) {
      case ReviewStep.BUILDING_OVERVIEW:
        return (
          <BuildingLevelOverview
            onContinue={handleBuildingOverviewContinue}
            isLoading={isLoading}
          />
        );
      case ReviewStep.ROOM_CRITERIA:
        return <RoomLevelDimensioningCriteria onApprove={handleRoomCriteriaApprove} />;
      case ReviewStep.SPECIFICATION:
      default:
        return (
          <SpecificationComplete
            onReviewValues={handleReviewValues}
            onProceed={handleLoadCalculation}
            isLoading={isLoading}
          />
        );
    }
  }

  return (
    <SpecificationComplete
      onReviewValues={handleReviewValues}
      onProceed={handleLoadCalculation}
      isLoading={isLoading}
    />
  );
};
